import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * <bundle-add-button> adds the main product's selected variant plus one
 * upsell variant to the cart in a single request, then notifies the rest
 * of the theme (cart drawer / cart count) via the standard cart event.
 */
class BundleAddButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.hasAttribute('disabled')) return;

    const mainForm = document.getElementById(this.dataset.mainForm);
    const mainId = mainForm?.querySelector('[name="id"]')?.value;
    const upsellId = this.dataset.upsellVariantId;

    if (!mainId || !upsellId) {
      console.error('bundle-add-button: missing variant id', { mainId, upsellId });
      return;
    }

    this.setAttribute('disabled', 'true');
    this.classList.add('loading');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          { merchandiseId: mainId, quantity: 1 },
          { merchandiseId: upsellId, quantity: 1 },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: mainId, quantity: 1 },
            { id: upsellId, quantity: 1 },
          ],
        }),
      }).then((r) => r.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        const ajaxCart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'bundle-add-button' },
        });
        return;
      }

      const ajaxCart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { didError: false, items: ajaxCart.items, source: 'bundle-add-button' },
      });
    } catch (error) {
      console.error('bundle-add-button: add to cart failed', error);
      deferredEventPromise.reject(error);
    } finally {
      this.removeAttribute('disabled');
      this.classList.remove('loading');
    }
  };
}

if (!customElements.get('bundle-add-button')) {
  customElements.define('bundle-add-button', BundleAddButton);
}
